export interface SearchSuggestionCategory {
  id?: string;
  name?: string;
  slug?: string;
}

export interface SearchSuggestionArticle {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  category?: SearchSuggestionCategory;
}

export interface PhraseSuggestion {
  id: string;
  value: string;
}

export interface ArticleSuggestion {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  categoryName?: string;
}

export interface SearchSuggestionsResult {
  phrases: PhraseSuggestion[];
  articles: ArticleSuggestion[];
}

interface RankKnowledgeArticlesInput {
  query: string;
  articles: SearchSuggestionArticle[];
  maxResults?: number;
}

interface BuildSearchSuggestionsInput {
  query: string;
  articles: SearchSuggestionArticle[];
  phrases?: string[];
  maxPhrases?: number;
  maxArticles?: number;
}

const COMPOUND_REPLACEMENTS: Array<[RegExp, string]> = [
  [/\btech[\s-]*panel\b/gu, 'techpanel'],
  [/\bтех[\s-]*панел(?:ь|и)?\b/gu, 'techpanel'],
  [/\bтеч[\s-]*панел(?:ь|и)?\b/gu, 'techpanel'],
  [/\bre[\s-]*born\b/gu, 'reborn'],
  [/\bреборн\b/gu, 'reborn'],
  [/\bsignal[\s-]*r\b/gu, 'signalr'],
  [/\bwire[\s-]*guard\b/gu, 'wireguard'],
  [/\bsetup[\s-]*wizard\b/gu, 'setupwizard'],
  [/\bgame[\s-]*updater\b/gu, 'gameupdater'],
  [/\bmain[\s-]*window\b/gu, 'mainwindow'],
  [/\badmin[\s-]*view\b/gu, 'adminview'],
  [/\bdisk[\s-]*reset\b/gu, 'diskreset'],
  [/\bfresh[\s-]*manage\b/gu, 'freshmanage'],
  [/\bi[\s-]*scsi\b/gu, 'iscsi'],
  [/\bng[\s-]*inx\b/gu, 'nginx'],
];

const collapseLetterAcronyms = (value: string) =>
  value.replace(/\b(?:[a-z][\s-]+){1,5}[a-z]\b/gu, (match) => match.replace(/[\s-]+/g, ''));

const normalize = (value: string) => {
  let normalized = value
    .normalize('NFKD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/\u0451/g, '\u0435');

  normalized = collapseLetterAcronyms(normalized);

  for (const [pattern, replacement] of COMPOUND_REPLACEMENTS) {
    normalized = normalized.replace(pattern, replacement);
  }

  return normalized
    .replace(/[^\p{L}\p{N}\s-]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
};

const compact = (value: string) => normalize(value).replace(/[\s-]+/g, '');

const unique = <T,>(items: T[], getKey: (item: T) => string) => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = getKey(item);
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

const buildQueryForms = (value: string) => {
  const normalized = normalize(value);
  const compacted = compact(value);

  return unique(
    [normalized, compacted].filter(Boolean),
    (item) => item,
  );
};

const scorePhrase = (phrase: string, query: string) => {
  const phraseForms = buildQueryForms(phrase);
  const queryForms = buildQueryForms(query);

  if (
    phraseForms.length === 0 ||
    queryForms.length === 0 ||
    phraseForms.some((phraseForm) => queryForms.includes(phraseForm))
  ) {
    return 0;
  }

  let bestScore = 0;

  for (const normalizedPhrase of phraseForms) {
    for (const normalizedQuery of queryForms) {
      if (normalizedPhrase.startsWith(normalizedQuery)) {
        bestScore = Math.max(bestScore, 300 - normalizedPhrase.length);
      } else if (normalizedPhrase.includes(` ${normalizedQuery}`)) {
        bestScore = Math.max(bestScore, 220 - normalizedPhrase.length);
      } else if (normalizedPhrase.includes(normalizedQuery)) {
        bestScore = Math.max(bestScore, 140 - normalizedPhrase.length);
      }
    }
  }

  return bestScore;
};

const scoreField = (
  value: string,
  queryForms: string[],
  weights: { startsWith: number; includesWord: number; includes: number },
) => {
  if (!value || queryForms.length === 0) {
    return 0;
  }

  const fieldForms = buildQueryForms(value);
  let bestScore = 0;

  for (const fieldForm of fieldForms) {
    for (const queryForm of queryForms) {
      if (fieldForm.startsWith(queryForm)) {
        bestScore = Math.max(bestScore, weights.startsWith);
      } else if (fieldForm.includes(` ${queryForm}`)) {
        bestScore = Math.max(bestScore, weights.includesWord);
      } else if (fieldForm.includes(queryForm)) {
        bestScore = Math.max(bestScore, weights.includes);
      }
    }
  }

  return bestScore;
};

const scoreArticle = (article: SearchSuggestionArticle, query: string) => {
  const queryForms = buildQueryForms(query);

  if (queryForms.length === 0) {
    return 0;
  }

  let score = 0;

  score += scoreField(article.title, queryForms, {
    startsWith: 420,
    includesWord: 320,
    includes: 240,
  });

  score += scoreField(article.slug, queryForms, {
    startsWith: 260,
    includesWord: 220,
    includes: 180,
  });

  score += scoreField(article.category?.name || '', queryForms, {
    startsWith: 140,
    includesWord: 120,
    includes: 100,
  });

  score += scoreField(article.excerpt || '', queryForms, {
    startsWith: 90,
    includesWord: 80,
    includes: 70,
  });

  score += scoreField(article.content || '', queryForms, {
    startsWith: 50,
    includesWord: 40,
    includes: 30,
  });

  return score;
};

const toExcerpt = (value?: string, limit = 110) => {
  if (!value) {
    return '';
  }

  const compactText = value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  if (compactText.length <= limit) {
    return compactText;
  }

  return `${compactText.slice(0, limit).trim()}...`;
};

export const buildKnowledgeSearchSuggestions = ({
  query,
  articles,
  phrases = [],
  maxPhrases = 5,
  maxArticles = 6,
}: BuildSearchSuggestionsInput): SearchSuggestionsResult => {
  const queryForms = buildQueryForms(query);

  if (queryForms.length === 0) {
    return { phrases: [], articles: [] };
  }

  const rankedArticles = articles
    .map((article) => ({
      article,
      score: scoreArticle(article, query),
    }))
    .filter((item) => item.score > 0)
    .sort((left, right) => right.score - left.score);

  const articleSuggestions = rankedArticles
    .slice(0, maxArticles)
    .map(({ article }) => ({
      id: article.id,
      title: article.title,
      slug: article.slug,
      excerpt: toExcerpt(article.excerpt || article.content),
      categoryName: article.category?.name,
    }));

  const articleTitleKeys = new Set(
    articleSuggestions.flatMap((article) => buildQueryForms(article.title)),
  );
  const phraseCandidates = unique(phrases.filter(Boolean), (value) => normalize(value));

  const phraseSuggestions = phraseCandidates
    .map((value) => ({
      id: `phrase:${compact(value) || normalize(value)}`,
      value,
      score: scorePhrase(value, query),
    }))
    .filter(
      (item) =>
        item.score > 0 &&
        !buildQueryForms(item.value).some((form) => articleTitleKeys.has(form)),
    )
    .sort((left, right) => right.score - left.score)
    .slice(0, maxPhrases)
    .map(({ id, value }) => ({ id, value }));

  return {
    phrases: phraseSuggestions,
    articles: articleSuggestions,
  };
};

export const rankKnowledgeArticles = ({
  query,
  articles,
  maxResults = 30,
}: RankKnowledgeArticlesInput): SearchSuggestionArticle[] => {
  const queryForms = buildQueryForms(query);

  if (queryForms.length === 0) {
    return articles.slice(0, maxResults);
  }

  return articles
    .map((article) => ({
      article,
      score: scoreArticle(article, query),
    }))
    .filter((item) => item.score > 0)
    .sort((left, right) => right.score - left.score)
    .slice(0, maxResults)
    .map((item) => item.article);
};
